/**
 * Central registry of all real-time pub/sub events pushed over WebSocket.
 *
 * The MainLayout WebSocket hook (useNotificationWebSocket) publishes when it
 * receives a frame, and interested pages subscribe to the events they care
 * about and refetch so their data updates immediately instead of waiting for
 * a polling fallback.
 *
 * Each event is a typed [subscribe, publish] pair backed by createPubSub.
 */

import { createPubSub } from './create-pub-sub'
import { EventAttendeeState } from 'graphql/generated/scheme'

// ─── Payloads ────────────────────────────────────────────────────────────────

export interface TicketCheckinPayload {
	eventId: string
}

export interface AttendeeStatePayload {
	eventId: string
	userId: string
	attendeeState: EventAttendeeState
}

export interface TicketUpdatedPayload {
	eventId: string
	eventTicketId?: string
}

export interface ScanAddedPayload {
	eventId: string
	ticketId: string
	scanningZoneId?: string
	scannedAt?: string
	scannedByUserId?: string
}

// ─── Events ──────────────────────────────────────────────────────────────────

/** Ticket scanned / check-in succeeded (CHECKIN_SUCCESS). */
export const { subscribe: subscribeTicketCheckin, publish: publishTicketCheckin } = createPubSub<TicketCheckinPayload>()

/** Attendee state changed (ATTENDEE_STATE_UPDATED). */
export const { subscribe: subscribeAttendeeState, publish: publishAttendeeState } = createPubSub<AttendeeStatePayload>()

/** Ticket balance / capacity changed (TICKET_UPDATED). */
export const { subscribe: subscribeTicketUpdated, publish: publishTicketUpdated } = createPubSub<TicketUpdatedPayload>()

/**
 * A scan was recorded for the event (SCAN_ADDED). Fires once per successful
 * scan — including repeat scans in multi-scan zones — so the scan-records audit
 * table can refresh live. Mirrors the backend `scanRecordAdded` payload.
 */
export const { subscribe: subscribeScanAdded, publish: publishScanAdded } = createPubSub<ScanAddedPayload>()
